import React, { useEffect, useMemo, useState } from 'react';
import { productivite as api } from '../services/api';
import { nb, pct1, moisAn, useFiltresVentes, PanneauVentes, usePanneau, basculer } from './ventesUtils';
import { formuleDe } from '../definitions';
import { SelecteurMaille, useRegroupement, construireArbre, axesDisponibles } from './regroupements';
import './VentesJour.css';  // rg-* du regroupement
import './Ventes.css';
import './Stock.css';   // st-tools, st-rech
import './Marche.css';  // étiquette mk-nous (non rapproché)

// ─── APV — Productivité (Auto Antilles) — 2026-10-05 ────────────────────────
// Pointages sur OR (A97 DATA, marts.pointages) : présence Kelio, heures
// pointées (OR et temps interne 999999) et facturées DataCar. Cinq ratios,
// toujours sur le mois ou le cumul (cf. services/productivite.js) :
//   occupation = pointé total ÷ présence · productivité = pointé OR ÷ présence
//   efficience = facturé ÷ pointé OR · rendement = facturé ÷ présence
//   temps interne = pointé 999999 ÷ présence.
// ⚠ La présence d'un REGROUPEMENT n'est pas la somme de ses membres : un
// compagnon partagé entre deux sociétés y serait compté deux fois. Le serveur
// la fournit dédoublonnée (`ensembles`) ; les autres heures s'additionnent.
// Le détail par compagnon (noms) est chargé à part : métier APV exigé.

const h = (v) => (v == null ? '—' : `${nb(v)} h`);
// En-tête avec sa formule au survol ; le détail est au clic droit sur le bloc
// (« D'où viennent ces chiffres ? », cf. definitions.js).
const thDef = (col) => {
  const f = formuleDe('apv-productivite', col.l);
  return <th key={col.c} className={f ? 'th-def' : undefined} title={f}>{col.l}</th>;
};
const COLONNES = [
  { c: 'presence', l: 'Présence', f: h },
  { c: 'pointeOr', l: 'Pointé OR', f: h },
  { c: 'interne', l: 'Interne', f: h },
  { c: 'facture', l: 'Facturé', f: h },
  { c: 'occupation', l: 'Occupation', f: pct1, taux: true },
  { c: 'productivite', l: 'Productivité', f: pct1, taux: true },
  { c: 'efficience', l: 'Efficience', f: pct1, taux: true },
  { c: 'rendement', l: 'Rendement', f: pct1, taux: true },
];
const ratio = (a, b) => (b > 0 ? Math.round((a / b) * 1000) / 10 : null);
// Ratios recalculés depuis les heures (jamais moyennés).
const avecRatios = (x) => ({
  ...x,
  occupation: ratio(x.pointeOr + x.interne, x.presence), productivite: ratio(x.pointeOr, x.presence),
  efficience: ratio(x.facture, x.pointeOr), rendement: ratio(x.facture, x.presence), tempsInterne: ratio(x.interne, x.presence),
});
// Au-delà de 100 % de productivité ou d'occupation : soit de la présence
// manque (compagnons non rapprochés de Kelio, piège 5), soit des pointages
// simultanés sur plusieurs OR (mesuré au rapide : Autodis à 111 % hors non
// rapprochés, 2026-10-05). L'écran le signale sans trancher.
const suspect = (col, x) => col.taux && (col.c === 'productivite' || col.c === 'occupation') && x[col.c] > 100;
const Cellules = ({ x }) => COLONNES.map((col) => (
  <td key={col.c} className={`${col.taux ? 'vt-taux' : ''}${suspect(col, x) ? ' tbl-neg' : ''}`}
    title={suspect(col, x) ? 'Plus de 100 % : présence manquante (compagnons non rapprochés de Kelio) ou pointages simultanés sur plusieurs OR' : undefined}>{col.f(x[col.c])}</td>
));

function Compagnons({ filtres }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [q, setQ] = useState('');
  const cle = JSON.stringify(filtres);
  useEffect(() => {
    let actif = true; setData(null); setErreur('');
    api.compagnons(filtres).then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Liste indisponible'); });
    return () => { actif = false; };
  }, [cle]);   // eslint-disable-line react-hooks/exhaustive-deps
  const liste = (data?.compagnons || []).filter((c) => !q || `${c.nom} ${c.matricule}`.toLowerCase().includes(q.toLowerCase()));
  const plusieurs = new Set((data?.compagnons || []).map((c) => c.societeCode)).size > 1;
  return (
    <div className="vt-card" data-definitions="apv-productivite">
      <div className="vt-card-h">
        <h2>Par compagnon</h2>
        <div className="st-tools">
          <input className="st-rech" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un compagnon…" aria-label="Rechercher un compagnon" />
        </div>
      </div>
      <div className="vt-card-b vt-tbl-wrap">
        {erreur ? <p className="st-note">{erreur}</p> : !data ? <div className="vt-skel" /> : (
          <table className="vt-tbl tbl-dense">
            <thead><tr>
              <th className="l">Compagnon</th>{plusieurs && <th className="l">Société</th>}<th className="l">Service</th><th>Jours</th>
              {COLONNES.map(thDef)}
            </tr></thead>
            <tbody>
              {liste.map((c) => (
                <tr key={`${c.societeCode}|${c.matricule}`}>
                  <td className="l">{c.nom || c.matricule}{c.nonRapproche && <span className="mk-nous" title="Matricule Kelio vide : nom différent entre DataCar et Kelio, ou homonyme — aucune présence dans le fichier">non rapproché</span>}</td>
                  {plusieurs && <td className="l">{c.societe}</td>}
                  <td className="l">{c.service || c.atelier || '—'}</td>
                  <td>{c.jours ? nb(c.jours) : '—'}</td>
                  <Cellules x={c} />
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function ApvProductivite() {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [ouvert, toggle] = usePanneau();
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const f = useFiltresVentes(societesDispo);
  const atelier = f.params.get('atelier') || '';

  useEffect(() => {
    let actif = true; setErreur('');
    api.charger({ periode: f.periode, mois: f.moisStr, societes: f.societesUrl, atelier })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [f.periode, f.moisStr, f.societesUrl, atelier]);

  const maille = useRegroupement();
  const feuilles = useMemo(() => (data?.societes || []).map((s) => ({ ...s, libelle: s.nom })), [data]);
  const axes = useMemo(() => axesDisponibles(feuilles), [feuilles]);
  const sommer = (membres) => {
    const som = (k) => membres.reduce((t, m) => t + (m[k] || 0), 0);
    const cleEns = membres.map((m) => m.code).sort().join(',');
    const presence = data?.ensembles?.[cleEns] ?? som('presence');
    return avecRatios({ presence, pointeOr: som('pointeOr'), interne: som('interne'), facture: som('facture'), nonRapproches: som('nonRapproches') });
  };
  const arbre = useMemo(
    () => construireArbre(feuilles, maille.niveau, sommer, maille.ouverts, (x) => x.presence),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feuilles, maille.niveau, maille.ouverts]);

  const Panneau = (
    <PanneauVentes ouvert={ouvert} onToggle={toggle} f={f} societesDispo={societesDispo}
      extra={{ choix: { titre: 'Atelier', valeurs: [{ code: '', nom: 'Tous' }, { code: 'Mécanique', nom: 'Mécanique' }, { code: 'Rapide', nom: 'Rapide' }],
        valeur: atelier, onChoisir: (c) => f.setFiltre({ atelier: c }) } }} />
  );
  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;
  if (data.indisponible || !data.total) {
    return <><main className="vt-page"><div className="bi-empty"><h2>Pointages non disponibles</h2><p>Le fichier des pointages sur OR n'est pas encore ingéré dans l'entrepôt.</p></div></main>{Panneau}</>;
  }
  const t = data.total;
  const K = [
    { l: 'Présence', v: h(t.presence), d: 'heures badgées (Kelio)' },
    { l: 'Productivité', v: pct1(t.productivite), d: 'pointé sur OR ÷ présence' },
    { l: 'Efficience', v: pct1(t.efficience), d: 'facturé ÷ pointé sur OR' },
    { l: 'Rendement', v: pct1(t.rendement), d: 'facturé ÷ présence' },
    { l: 'Occupation', v: pct1(t.occupation), d: 'pointé total ÷ présence' },
    { l: 'Temps interne', v: pct1(t.tempsInterne), d: `${h(t.interne)} sur l'OR 999999` },
  ];

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div className="en-construction-titre"><h1>APV — Productivité</h1>
            <span className="en-construction" title="Rapport en cours de mise au point : les chiffres et les définitions peuvent encore évoluer">
              <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path d="M10.3 3.9 2.6 18a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" fill="currentColor" opacity=".18"/><path d="M10.3 3.9 2.6 18a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M8 17h8M9.5 13.5h5M11 10h2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
              En construction !
            </span>
          </div>
          <div className="vt-asof"><b>{data.periode}</b>{atelier ? <span> · {atelier.toLowerCase()}</span> : null}<br /><span>pointages depuis le 02/01/2026</span></div>
        </div>

        <div className="vt-kpis" style={{ gridTemplateColumns: 'repeat(6,1fr)' }}>
          {K.map((k) => <div className="vt-kpi" key={k.l}><div className="l">{k.l}</div><div className="v">{k.v}</div><div className="d"><small>{k.d}</small></div></div>)}
        </div>

        <div className="vt-card" data-definitions="apv-productivite">
          <div className="vt-card-h">
            <h2>Par société</h2>
            <div className="st-tools">
              <span className="hint">
                {t.nonRapproches ? `${nb(t.nonRapproches)} compagnon${t.nonRapproches > 1 ? 's' : ''} non rapproché${t.nonRapproches > 1 ? 's' : ''} de Kelio : leur pointé n'a pas de présence en face` : ''}</span>
              <SelecteurMaille niveau={maille.niveau} setNiveau={maille.setNiveau} dispo={axes} />
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-tbl">
              <thead><tr><th className="l">Société</th>{COLONNES.map(thDef)}<th>Non rapprochés</th></tr></thead>
              <tbody>
                {arbre.map((s) => {
                  const cible = s.feuille ? s.code : (s.membres || []).join(',');
                  return (
                    <tr key={s.cle} className={`tbl-clic rg-n${s.niveau}${f.societesUrl === cible ? ' actif' : ''}`}
                      onClick={() => f.setFiltre({ societes: basculer(f.societesUrl, cible) })}>
                      <td className="l">
                        <span className="rg-in" style={{ width: s.niveau * 14 }} />
                        {s.feuille ? <span className="rg-chev vide" /> : (
                          <button type="button" className={`rg-btn${maille.ouverts.has(s.cle) ? ' on' : ''}`}
                            title={maille.ouverts.has(s.cle) ? 'Replier' : 'Voir le détail par société'} aria-label="Déplier"
                            onClick={(e) => { e.stopPropagation(); maille.basculer(s.cle); }}>▶</button>
                        )}
                        {s.libelle || s.nom}
                        {!s.feuille && <span className="rg-nb">{s.nbEnfants}</span>}
                      </td>
                      <Cellules x={s} />
                      <td>{s.nonRapproches ? nb(s.nonRapproches) : '·'}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot><tr><td className="l">Total</td><Cellules x={t} /><td>{t.nonRapproches ? nb(t.nonRapproches) : '·'}</td></tr></tfoot>
            </table>
          </div>
        </div>

        <div className="vt-card" data-definitions="apv-productivite">
          <div className="vt-card-h"><h2>Par atelier</h2></div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-tbl">
              <thead><tr><th className="l">Atelier</th>{COLONNES.map(thDef)}</tr></thead>
              <tbody>
                {data.parAtelier.map((a) => (
                  <tr key={a.atelier} className={`tbl-clic${atelier === a.atelier ? ' actif' : ''}`} onClick={() => f.setFiltre({ atelier: basculer(atelier, a.atelier) })}>
                    <td className="l">{a.atelier}</td><Cellules x={a} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="vt-card" data-definitions="apv-productivite">
          <div className="vt-card-h"><h2>Par mois</h2><span className="hint">le pointé est daté du jour du travail, le facturé du jour de la facture : les ratios se lisent au mois</span></div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-tbl">
              <thead><tr><th className="l">Mois</th>{COLONNES.map(thDef)}</tr></thead>
              <tbody>
                {data.parMois.map((m) => <tr key={m.ym}><td className="l">{moisAn(m.ym)}</td><Cellules x={m} /></tr>)}
              </tbody>
              <tfoot><tr><td className="l">Total</td><Cellules x={t} /></tr></tfoot>
            </table>
          </div>
        </div>

        <Compagnons filtres={{ periode: f.periode, mois: f.moisStr, societes: f.societesUrl, atelier }} />
      </main>
      {Panneau}
    </>
  );
}
